import * as React from 'react';

import { SidebarSectionType } from 'src/components/sidebarSection/SidebarSection';
import { ProjectSidebarTileId } from 'src/constants/mosaic';
import { ProjectResourceType } from 'src/constants/project';
import { ProjectSidebarFileUploadCard } from 'src/containers/ProjectSidebar/components/ProjectSidebarFileUploadCard';
import { ProjectSidebarVirtualGraphCard } from 'src/containers/ProjectSidebar/components/ProjectSidebarVirtualGraphCard';
import { isDraftDiagramSelector } from 'src/selectors/diagramSnapshot';
import { projectResourceListSelector } from 'src/selectors/fullResourceList';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useResourcesSection = (filter: string): SidebarSectionType => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const allItems = useReduxSelector(projectResourceListSelector(projectId));

  const addDisabled = useReduxSelector(isDraftDiagramSelector(projectId));

  const onAdd = React.useCallback(() => {
    dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: true }));
  }, [dispatch]);

  return React.useMemo(() => {
    const trimmed = filter.trim().toLowerCase();
    const items = allItems
      .map((item, index) => ({ item, index }))
      .filter(
        ({ item }) => !trimmed || item.text.toLowerCase().includes(trimmed)
      )
      .map(({ item, index }) => {
        if (item.type === ProjectResourceType.FILE_UPLOAD) {
          return (
            <ProjectSidebarFileUploadCard
              key={item.value}
              fileId={item.value}
              idx={index}
            />
          );
        }
        if (item.type === ProjectResourceType.VIRTUAL_GRAPH) {
          return (
            <ProjectSidebarVirtualGraphCard
              key={item.value}
              idx={index}
              virtualGraphId={item.value}
            />
          );
        }
        return null;
      });

    return {
      id: ProjectSidebarTileId.RESOURCES,
      addDisabled,
      addDisabledTooltip:
        copy.components.voicebox.disabledFor[ProjectSidebarTileId.RESOURCES],
      allCount: allItems.length,
      items,
      itemsCount: items.length,
      title: copy.components.project.resources.title,
      onAdd,
    };
  }, [filter, addDisabled, onAdd, allItems]);
};
